import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import { Link, useLocation, useParams } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import UserLayout from '../components/user/UserLayout';
import PaginationControls from '../components/common/PaginationControls';
import api from '../lib/api';

function AgentActivityDetailInner() {
  const { agentId } = useParams();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin/');
  const [payload, setPayload] = useState({ results: [], count: 0, next: null, previous: null, summary: {}, agent: null });
  const [filters, setFilters] = useState({ status: '', q: '', start_date: '', end_date: '', page: 1, page_size: 20 });
  const [msg, setMsg] = useState('');

  const load = async (overrides = {}) => {
    const nextFilters = { ...filters, ...overrides };
    setFilters(nextFilters);
    try {
      const { data } = await api.get(`/agent/handover/agent-detail/${agentId}/`, {
        params: {
          status: nextFilters.status || undefined,
          q: nextFilters.q || undefined,
          start_date: nextFilters.start_date || undefined,
          end_date: nextFilters.end_date || undefined,
          page: nextFilters.page,
          page_size: nextFilters.page_size,
        },
      });
      setPayload(data || { results: [], count: 0, next: null, previous: null, summary: {}, agent: null });
      setMsg('');
    } catch (e) {
      setMsg(e?.response?.data?.error || 'Failed to load agent activity details.');
    }
  };

  useEffect(() => {
    load({ page: 1 });
  }, [agentId]);

  const title = useMemo(
    () => `Agent Activity · ${payload?.agent?.name || `#${agentId}`}`,
    [payload?.agent?.name, agentId]
  );

  const content = (
    <>
      {msg ? <Alert variant="danger">{msg}</Alert> : null}
      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={2}>
              <Form.Label>Status</Form.Label>
              <Form.Select value={filters.status} onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}>
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="picked">Picked</option>
                <option value="active">Active</option>
                <option value="resolved">Resolved</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label>Search</Form.Label>
              <Form.Control value={filters.q} onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))} placeholder="thread / requester" />
            </Col>
            <Col md={2}>
              <Form.Label>Start</Form.Label>
              <Form.Control type="date" value={filters.start_date} onChange={(e) => setFilters((p) => ({ ...p, start_date: e.target.value }))} />
            </Col>
            <Col md={2}>
              <Form.Label>End</Form.Label>
              <Form.Control type="date" value={filters.end_date} onChange={(e) => setFilters((p) => ({ ...p, end_date: e.target.value }))} />
            </Col>
            <Col md={3} className="d-flex gap-2">
              <Button className="w-100" onClick={() => load({ page: 1 })}>Apply</Button>
              <Button
                variant="outline-secondary"
                onClick={() => {
                  const params = new URLSearchParams({
                    status: filters.status || '',
                    q: filters.q || '',
                    start_date: filters.start_date || '',
                    end_date: filters.end_date || '',
                    export: 'csv',
                  });
                  window.open(`/api/agent/handover/agent-detail/${agentId}/?${params.toString()}`, '_blank');
                }}
              >
                Export CSV
              </Button>
            </Col>
          </Row>
          <div className="small text-muted mt-3">
            Picked: <strong>{payload?.summary?.picked_count ?? 0}</strong> · Resolved: <strong>{payload?.summary?.resolved_count ?? 0}</strong> · Replied: <strong>{payload?.summary?.replied_count ?? 0}</strong>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <div className="table-responsive">
            <Table hover className="align-middle mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Thread</th>
                  <th>Channel</th>
                  <th>Status</th>
                  <th>Requester</th>
                  <th>Updated</th>
                  <th>View</th>
                </tr>
              </thead>
              <tbody>
                {(payload?.results || []).length ? (
                  payload.results.map((row) => (
                    <tr key={row.id}>
                      <td>#{row.id}</td>
                      <td>{row.thread_id || '-'}</td>
                      <td>{row.channel}</td>
                      <td>{row.status}</td>
                      <td>{row.requester_name || row.requester_email || 'Guest'}</td>
                      <td>{row.updated_at ? new Date(row.updated_at).toLocaleString() : '-'}</td>
                      <td>
                        {isAdmin ? (
                          <Link to={`/admin/handover/${row.id}`}>Open thread</Link>
                        ) : (
                          <Link to={`/dashboard/agent-inbox?handover_id=${row.id}`}>Open thread</Link>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">No rows found.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
          <div className="mt-3">
            <PaginationControls
              page={filters.page}
              hasPrev={!!payload.previous}
              hasNext={!!payload.next}
              onPrev={() => payload.previous && load({ page: Math.max(1, filters.page - 1) })}
              onNext={() => payload.next && load({ page: filters.page + 1 })}
            />
          </div>
        </Card.Body>
      </Card>
    </>
  );

  if (isAdmin) {
    return <AdminLayout title={title} subtitle="Full lifecycle audit for one support agent">{content}</AdminLayout>;
  }
  return <UserLayout title={title} subtitle="Full lifecycle audit for one support agent">{content}</UserLayout>;
}

export default function AgentActivityDetailPage() {
  return <AgentActivityDetailInner />;
}
